"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const util_1 = require("../util");
const { POST, PUT } = util_1.httpMethods;
/**
 * @classdesc Represents the class interfacing pseudonode touch related API-I endpoints
 * @class
 */
class PseudonodeTouch {
    constructor(api) {
        this.api = api;
    }
    /**
     * Install Node from Pseudonode according to the parameters provided
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#pseudonodes-install-node-from-pseudonode-post}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!number} args.pseudonodeId - Required. The specific ids of the pseudonode being used to install upon
     * @param {!string} args.nodeIdentifier - Required. The node identifier being installed
     * @param {!number} args.customerId - Required. The id of customer
     * @param {!string} args.location - Required. WKT of the lat/lng of the node
     * @param {!number} args.maintenanceOwnerId - Required. The id of maintenance owner provider
     * @param {?string} args.force - Optional. Set to handle node identifier conflicts: replan | remove
     * @param {?string} args.name - Optional. The name of the new node
     * @param {?number} args.gpsAccuracy - Optional. The gps accuracy recorded when installing this node
     * @param {?string} args.antennaType - Optional. Sets the antenna type, DN only: DEFAULT | DIRECTIONAL | OMNIDIRECTIONAL
     * @param {?string[]} args.imageUrls - Optional. An array of image urls for the node
     * @param {?string} args.trapCameraOrientation - Optional. Sets the camera orientation value of the trap if different to that of the primary lure: HORIZONTAL | VERTICAL
     * @param {?object} args.dispenser={} - Optional. An object containing the dispenser arguments
     * @param {?number} args.dispenser.revision - Optional. The revision of the node. SN_SCD only
     * @param {?number} args.dispenser.formulationId - Optional. The formulation id of a formulation can installed with the node. SCD only
     * @param {?number} args.dispenser.formulationBatchId - Optional. The formulation batch id of a formulation can installed with the node. SCD only
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return a response object or an error with the problem.
     */
    async installNodeFromPseudonode(args) {
        const { pseudonodeId, nodeIdentifier, customerId, location, maintenanceOwnerId, force, name, gpsAccuracy, antennaType, imageUrls, trapCameraOrientation, dispenser: { revision, formulationId, formulationBatchId } = {}, } = args;
        const methodName = 'installNodeFromPseudonode';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'pseudonodeId', pseudonodeId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'pseudonodeId', pseudonodeId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'nodeIdentifier', nodeIdentifier, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'maintenanceOwnerId', maintenanceOwnerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'maintenanceOwnerId', maintenanceOwnerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'force', force, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'name', name, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'gpsAccuracy', gpsAccuracy, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'antennaType', antennaType, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'imageUrls', imageUrls, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'trapCameraOrientation', trapCameraOrientation, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'revision', revision, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'formulationId', formulationId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'formulationBatchId', formulationBatchId, util_1.ValidationTypes.INTEGER),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/pseudonodes/${pseudonodeId}/install`;
        const input = {
            header: {
                customerId,
            },
            query: {
                force,
            },
            body: {
                nodeIdentifier,
                location,
                maintenanceOwnerId,
                name,
                gpsAccuracy,
                antennaType,
                imageUrls,
                trapCameraOrientation,
                dispenser: {
                    revision,
                    formulationId,
                    formulationBatchId,
                },
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(POST, route, endPointInput);
    }
    /**
     * Reposition multiple pseudonodes according to the parameters provided
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#pseudonodes-reposition-pseudonodes-put}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!object} args.pseudonodes={} - Required. The specific ids of the pseudonode being used to reposition
     * @return {promise}
     */
    async repositionPseudonodes(args) {
        const { pseudonodes, customerId } = args;
        const methodName = 'repositionPseudonodes';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'pseudonodes', pseudonodes, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'pseudonodes', pseudonodes, util_1.ValidationTypes.OBJECT),
        ];
        (0, util_1.validateParams)(validations);
        const route = '/pseudonodes/reposition';
        const input = {
            header: {
                customerId,
            },
            body: {
                pseudonodes,
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(PUT, route, endPointInput);
    }
}
exports.default = PseudonodeTouch;
//# sourceMappingURL=pseudonode-touch.js.map